import { ChangeDetectionStrategy, Component, effect, inject, Signal, untracked } from '@angular/core';
import { AuthService } from 'src/app/core/services/authentication.service';
import { NavigationService } from 'src/app/core/services/navigation.service';
import { AuthUserStateService } from 'src/app/core/state/auth-user-state.service';
import { GlobalStateService } from 'src/app/core/state/global-state.service';
import { StandaloneDefaultImports } from 'src/app/shared/ui/standalone-default-imports';
import { Constants } from 'src/app/core/constants/constants';
import { SIDEMENU_ITEMS, SidemenuItem } from 'src/app/core/constants/sidemenu.config';

@Component({
    selector: 'app-sidemenu',
    imports: [...StandaloneDefaultImports],
    templateUrl: './sidemenu.component.html',
    styleUrls: ['./sidemenu.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '(window:resize)': 'onResize()'
    }
})
export class SidemenuComponent {
    // Section: SERVICES / DEPENDENCIES
    //----------------------------------------------------------------------
    private readonly authService = inject(AuthService);
    private readonly globalState = inject(GlobalStateService);
    private readonly authUserState = inject(AuthUserStateService);
    private readonly navigationService = inject(NavigationService);

    // Section: CONSTANTS
    //----------------------------------------------------------------------
    readonly CATEGORY_CONTAINER_STATE = Constants.CATEGORY_CONTAINER_STATE;
    private readonly initialSidemenuItems: SidemenuItem[] = SIDEMENU_ITEMS;

    // Section: SIGNALS, OBSERVABLES AND STATE REACTIVE
    //----------------------------------------------------------------------
    authenticated: Signal<boolean> = this.authUserState.isAuthenticated;
    isSidemenuVisible: Signal<boolean> = this.globalState.isSidemenuVisible;
    openedMenuMobileSize: Signal<boolean> = this.globalState.openedMenuMobileSize;
    currentUrl: Signal<string | null> = this.navigationService.currentUrl;

    // Section: PUBLIC PROPERTIES
    //----------------------------------------------------------------------
    menuLinks: SidemenuItem[] = [];
    submenuLinks: SidemenuItem[] = [];
    isMobileSize = false;
    categoryContainerState = Constants.CATEGORY_CONTAINER_STATE.CLOSED;
    isSubmenuVisible = false;
    isSideMenuMoreItemVisible = false;
    inmobile = this.globalState.inmobile();

    // Section: PRIVATE PROPERTIES
    //----------------------------------------------------------------------
    private sidemenuItems: SidemenuItem[] = [];

    // Section: CONSTRUCTOR
    //----------------------------------------------------------------------
    constructor() {
        effect(() => {
            if (this.authenticated()) {
                untracked(() => this.initializeSidemenu());
            }
        });
    }

    // Section: PUBLIC METHODS
    //----------------------------------------------------------------------
    onResize(): void {
        this.setItemsMenus();
        this.updateSidemenuVisibility();
    }

    openSidemenu(): void {
        this.globalState.setSidemenuVisible(true);
        this.globalState.setInmobile(false);
        this.globalState.setOpenedMenuMobileSize(!!this.isMobileSize);
    }

    closeSidemenu(): void {
        this.globalState.setSidemenuVisible(false);
        this.globalState.setInmobile(true);
        this.globalState.setOpenedMenuMobileSize(false);
        this.movementMenuFn();
    }

    checkActive(link: any): boolean {
        const currentUrlValue = this.currentUrl();
        if (!currentUrlValue && !link?.activeRoutes) {
            return false;
        }
        const currentPathName = (currentUrlValue ?? '').split('?')[0];
        return !!link.activeRoutes?.some((routePattern: string) => {
            if (routePattern.includes(':')) {
                // Convert the parameterized path pattern to a regular expression
                // Example: '/time-tracking/:id/detail' becomes /^\/time-tracking\/[^/]+\/detail$/
                // ^ indicates the start of the string
                // $ indicates the end of the string
                // [^/]+ matches one or more non-slash characters (for the parameter value)
                const regexPattern = '^' + routePattern.replace(/\/:[^/]+/g, '/[^/]+') + '$';
                const regex = new RegExp(regexPattern);
                return regex.test(currentPathName);
            } else {
                // For paths without parameters, use exact match or startsWith for subpaths
                return currentPathName === routePattern || currentPathName.startsWith(routePattern + '/');
            }
        });
    }

    checkActiveMenuMore(): boolean {
        const currentUrlValue = this.currentUrl();
        if (!currentUrlValue) return false;

        const currentPathname = (currentUrlValue ?? '').split('?')[0];
        return (
            this.submenuLinks?.some((item) =>
                item.activeRoutes?.some((routePattern: string) => {
                    if (routePattern.includes(':')) {
                        const regexPattern = '^' + routePattern.replace(/\/:[^/]+/g, '/[^/]+') + '$';
                        const regex = new RegExp(regexPattern);
                        return regex.test(currentPathname);
                    } else {
                        return currentPathname === routePattern || currentPathname.startsWith(routePattern + '/');
                    }
                })
            ) ?? false
        );
    }

    toggleSubmenuVisibility(): void {
        this.isSubmenuVisible = !this.isSubmenuVisible;
    }

    redirectTo(url: string): void {
        this.isSubmenuVisible = false;
        this.navigationService.navigateToUrl(url);
    }

    // Section: PRIVATE METHODS
    //----------------------------------------------------------------------
    private checkAvailableItems(): void {
        this.initialSidemenuItems.forEach((item) => {
            if (!item.module || this.authService.checkIfModuleIsActiveForUserCompany(item.module)) {
                item.isAccessible = this.authService.checkIfItemIsAvailableForLoggedUser(item);
                this.sidemenuItems.push(item);
            }
        });
    }

    private getMenuActiveItems(): number {
        const menuElement = document.getElementById('sidemenu');

        const menuHeight = menuElement?.clientHeight || document.documentElement.clientHeight;
        const horizontalScrollbarHeight = window.innerHeight - document.documentElement.clientHeight;
        const sidemenuMarginSize = 114;
        const sidemenuItemHeight = 44;
        const numItemsMenuActive = Math.floor((menuHeight - sidemenuMarginSize - horizontalScrollbarHeight) / sidemenuItemHeight);
        const minNumItemsMenuActive = 5;
        return Math.max(numItemsMenuActive, minNumItemsMenuActive);
    }

    private setItemsMenus(): void {
        this.menuLinks = [...this.sidemenuItems];
        const menuActiveItems = this.getMenuActiveItems();
        this.submenuLinks = this.menuLinks.splice(menuActiveItems, this.menuLinks.length - menuActiveItems);
        this.isSideMenuMoreItemVisible = this.submenuLinks.length > 0;
    }

    private initializeSidemenu(): void {
        this.sidemenuItems = [];
        this.checkAvailableItems();
        this.setItemsMenus();
        this.updateSidemenuVisibility();
    }

    private updateSidemenuVisibility(): void {
        this.isMobileSize = window['innerWidth'] < 1280;
        if (!this.inmobile) {
            if (this.isMobileSize) {
                this.globalState.setSidemenuVisible(!!this.openedMenuMobileSize());
            } else {
                this.globalState.setSidemenuVisible(true);
                this.globalState.setOpenedMenuMobileSize(false);
            }
        } else {
            this.globalState.setSidemenuVisible(false);
        }
    }

    private movementMenuFn(): void {
        // this._rootScope.movementMenu = false;
        // setTimeout(() => {
        //     this._rootScope.movementMenu = true;
        // }, 400);
    }
}
